import types from "./types";
import { updateObject } from "utils";
import moment from "moment";
import { resolveNonErsUser } from "utils/user/verification";

const initialState = {
    createdAt: null,
    email: null,
    language: "English",
    name: "",
    permissions: null,
    uid: null,
    units: "Metric",
    loading: false,
    modelDirLoading: false,
    commDirLoading: false,
    batchDirLoading: false,
    isParametricDirLoading: true,
    error: null,
    folders: [],
    modelDir: {},
    commDir: {},
    codeLib: {},
    userMeta: {},
    parametricDir: {},
    lastActivity: null,
    didUserSawNewDrawing: false,
    registrationComplete: true,
    reqTeamToCreate: false,
    allowPayments: false,
};

export default (state = initialState, action) => {
    switch (action.type) {
        case types.GET_USER_DATA:
            return userInit(state, action);
        case types.GET_USER_DIR:
            return userDir(state, action);
        case types.GET_USER_COMM_DIR:
            return userCommDir(state, action);
        case types.GET_USER_BATCH_DIR:
            return userBatchDir(state, action);
        case types.GET_USER_DATA_START:
            return getUserStart(state, action);
        case types.GET_USER_DATA_FAIL:
            return getUserFail(state, action);
        case types.GET_USER_CODE_LIB:
            return userCodeLib(state, action);
        case types.SET_CODE_IN_LIB:
            return setCodeInLib(state, action);
        case types.REMOVE_FROM_DIR:
            return removeFromDir(state, action);
        case types.UPDATE_MODEL_IN_DIR:
            return updateModelInDir(state, action);
        case types.REMOVE_FROM_COMM_DIR:
            return removeFromCommDir(state, action);
        case types.UPDATE_USER:
            return updateUser(state, action);
        case types.TOGGLE_DIR_LOADING:
            return toggleDirLoading(state, action);
        case types.TOGGLE_COMM_DIR_LOADING:
            return toggleCommDirLoading(state, action);
        case types.TOGGLE_BATCH_DIR_LOADING:
            return toggleBatchDirLoading(state, action);
        case types.UPDATE_USER_META:
            return updateUserMeta(state, action);
        case types.UPDATE_USER_PREFERENCES:
            return updateUserPreferences(state, action);
        case types.REMOVE_CODE_FROM_LIB:
            return removeCodeFromLib(state, action);
        case types.FETCH_PARAMETRIC_ANALYSIS_DIR_START:
            return updateObject(state);
        case types.FETCH_PARAMETRIC_ANALYSIS_DIR_SUCCESS:
            return fetchParametricAnalysisDirSuccess(state, action);
        case types.FETCH_PARAMETRIC_ANALYSIS_DIR_FAIL:
            return updateObject(state, {
                isParametricDirLoading: false,
                parametricDir: {},
                parametricDirError: action.payload,
            });
        case types.TOGGLE_PARAMETRIC_ANALYSIS_DIR_LOADING:
            return updateObject(state, { isParametricDirLoading: action.payload });
        default:
            return state;
    }
};

const updateUser = (state, action) => updateObject(state, action.data);

const updateUserMeta = (state, action) =>
    updateObject(state, {
        userMeta: updateObject(state.userMeta, action.data),
    });

const updateUserPreferences = (state, action) =>
    updateObject(state, {
        userPreferences: updateObject(state.userPreferences, action.data),
    });

const getUserStart = (state, action) => updateObject(state, { loading: true });

const toggleDirLoading = (state, action) => updateObject(state, { modelDirLoading: action.modelDirLoading });

const toggleCommDirLoading = (state, action) => updateObject(state, { commDirLoading: action.commDirLoading });

const toggleBatchDirLoading = (state, action) => updateObject(state, { batchDirLoading: action.batchDirLoading });

const getUserFail = (state, action) => updateObject(state, { loading: false, error: action.error });

const removeFromDir = (state, action) => {
    const { modelDir: { models: { [action.modelId]: _, ...rest } = {} } = {} } = state;

    return updateObject(state, {
        modelDir: {
            ...state.modelDir,
            models: {
                ...rest,
            },
        },
    });
};

const updateModelInDir = (state, action) => {
    const existingModel = state.modelDir?.models?.[action.modelId];
    if (!existingModel) return state;

    return updateObject(state, {
        modelDir: {
            ...state.modelDir,
            models: {
                ...state.modelDir.models,
                [action.modelId]: {
                    ...existingModel,
                    ...action.updates,
                },
            },
        },
    });
};

const removeFromCommDir = (state, action) => {
    const { commDir: { communities: { [action.communityId]: _, ...rest } = {} } = {} } = state;

    return updateObject(state, {
        commDir: {
            ...state.commDir,
            communities: {
                ...rest,
            },
        },
    });
};

const userDir = (state, action) =>
    updateObject(state, {
        modelDir: {
            folders: action.modelDir.folders,
            models: action.modelDir.models,
        },
    });

const userCommDir = (state, action) =>
    updateObject(state, {
        commDir: {
            folders: action.commDir.folders,
            communities: action.commDir.communities,
        },
    });

const userBatchDir = (state, action) =>
    updateObject(state, {
        batchDir: {
            batches: action?.batchDir?.batches || {},
        },
    });

const userCodeLib = (state, action) =>
    updateObject(state, {
        codeLib: action.codeLib,
    });

const setCodeInLib = (state, action) => {
    const { codeLib = {} } = state;
    const { [action.componentType]: componentType = {} } = codeLib;
    const { codes = {} } = componentType;

    return updateObject(state, {
        codeLib: updateObject(codeLib, {
            [action.componentType]: updateObject(componentType, {
                codes: updateObject(codes, {
                    [action.codeRef]: action.code,
                }),
            }),
        }),
    });
};

const removeCodeFromLib = (state, action) => {
    const { codeLib = {} } = state;
    const { [action.componentType]: componentType = {} } = codeLib;
    const { codes: { [action.codeRef]: codeToDelete, ...rest } = {} } = componentType;

    return updateObject(state, {
        codeLib: updateObject(codeLib, {
            [action.componentType]: updateObject(componentType, {
                codes: rest,
            }),
        }),
    });
};

const userInit = (state, action) =>
    updateObject(state, {
        createdAt: action.userData.createdAt.toDate(),
        email: action.userData.email,
        language: action.userData.language,
        name: action.userData.name,
        permissions: action.userData.permissions,
        uid: action.userData.uid,
        units: action.userData.units,
        userMeta: action.userData.userMeta,
        userPreferences: action.userData.userPreferences,
        loading: false,
        error: false,
        lastActivity: moment(action.userData.lastActivity).format(),
        userPermissions: action.userData.userPermissions,
        nonErsUser: resolveNonErsUser(action.userData),
        verifiedF280User: action.userData?.verifiedF280User || false,
        didUserSawNewDrawing: action.userData?.didUserSawNewDrawing || false,
        creditSummary: action.userData?.creditSummary || {},
        isEmailConfirmed: action.userData?.isEmailConfirmed !== undefined ? action.userData?.isEmailConfirmed : true,
        isUserVerified: action.userData?.isUserVerified !== undefined ? action.userData?.isUserVerified : true,
        registrationComplete: action.userData?.registrationComplete !== false,
        reqTeamToCreate: action.userData?.reqTeamToCreate === true,
        allowPayments: Boolean(action.userData?.allowPayments),
    });

const fetchParametricAnalysisDirSuccess = (state, action) => {
    return updateObject(state, {
        parametricDir: action.payload,
        isParametricDirLoading: false,
    });
};
